import { FC, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { ModalDialog, TextboxTemplate } from "@ryde/controls";
import {
  useReportActions,
  useReportsContext,
  useReportsNavigation,
  useSaveReport,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  errors,
  ISaveReportForm,
  Log,
  reportModalEditAtom,
  ReportModalEditModeEnum,
  ReportOwnerCategoryEnum,
  useFormats,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import {
  ReportOwnerCategorySwitch,
  SaveReportContentContainer,
  SaveReportContentContainerFooter,
  saveReportValidationSchema,
} from "./parts";

export const SaveReportDialog: FC = () => {
  const { t } = useTranslation();
  const { reportDto, report, ownerCategory } = useReportsContext();
  const [modalOpen, setModalOpen] = useRecoilState(reportModalEditAtom);
  const { navigateReportById } = useReportsNavigation();
  const { formatDateTimeNormalized } = useFormats();
  const resolver = useYupValidationResolver(
    saveReportValidationSchema,
    "report",
  );
  const methods = useForm<Partial<ISaveReportForm>>({
    mode: "all",
    resolver,
  });

  const {
    getValues,
    trigger,
    reset,
    clearErrors,
    formState: { isValid },
  } = methods;
  const { fetchReport } = useReportActions();
  const { generateDefaultReportName, saveReport } = useSaveReport();

  const closeModal = (): void => {
    setModalOpen(false);
  };

  const isEdit = useMemo(() => {
    return modalOpen === ReportModalEditModeEnum.Edit;
  }, [modalOpen]);

  useEffect(() => {
    if (!report) {
      Log.errorAndToast(t("errors.invalidSourceReport"));
      return;
    }

    clearErrors();

    if (isEdit) {
      const reportEditDefaultFormData: ISaveReportForm = {
        name: report.name,
        description: report.description,
        ownerCategory,
      };
      reset(reportEditDefaultFormData);
    } else {
      const generatedName = generateDefaultReportName(reportDto);
      const reportCopyDefaultFormData: ISaveReportForm = {
        name: generatedName,
        description: report.description,
        ownerCategory: ReportOwnerCategoryEnum.UserOnly,
      };
      reset(reportCopyDefaultFormData);
    }
  }, [
    clearErrors,
    generateDefaultReportName,
    isEdit,
    ownerCategory,
    report,
    reportDto,
    reset,
    t,
  ]);

  const handleSaveReport = useEvent(async () => {
    const formIsValid = await trigger();

    if (!formIsValid) {
      Log.warnAndToast(errors.reports.invalidReportFormData);
      return;
    }

    if (!reportDto) {
      Log.warnAndToast("errors.invalidSourceReport");
      return;
    }

    const updatedReport = getValues();
    const result = await saveReport(updatedReport, reportDto, isEdit);

    if (!result.success) {
      warnAndToastSimpleResult(result, t);
    } else {
      Log.infoAndToast(
        t(
          isEdit
            ? "report.REPORT_UPDATED_SUCCESSFULLY"
            : "report.REPORT_CREATED_SUCCESSFULLY",
        ),
      );
      const newReportId = result.data?.id;

      if (!newReportId) {
        Log.warnAndToast(t(errors.common.invalidAPIResponse));
        return;
      }

      if (isEdit) {
        fetchReport({});
      } else {
        navigateReportById(newReportId, false);
      }

      closeModal();
    }
  });

  return (
    <FormProvider {...methods}>
      <ModalDialog
        isModalOpen={Boolean(modalOpen)}
        closeModalClick={closeModal}
        title={t(isEdit ? "report.UPDATE_REPORT" : "report.ADD_REPORT")}
        saveClick={handleSaveReport}
        isSaveButtonDisabled={!isValid}
      >
        <SaveReportContentContainer>
          <TextboxTemplate name={"name"} />
          <TextboxTemplate name={"description"} />
          <ReportOwnerCategorySwitch name={"ownerCategory"} />
          {isEdit && (
            <SaveReportContentContainerFooter>
              {report?.createdAt && (
                <label className="created_info">
                  {`${t("general.CREATED_AT")} ${formatDateTimeNormalized(report.createdAt)}`}
                </label>
              )}
              {report?.createdAt && report?.updatedAt && " | "}
              {report?.updatedAt && (
                <label className="updated_info">
                  {`${t("general.UPDATED_AT")} ${formatDateTimeNormalized(report.updatedAt)}`}
                </label>
              )}
            </SaveReportContentContainerFooter>
          )}
        </SaveReportContentContainer>
      </ModalDialog>
    </FormProvider>
  );
};
